import { Plus } from 'lucide-react'

import { type ReactNode, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  invitationService,
  type TeamMemberOrInvitation,
} from '@/services/invitations'

interface InviteUserDialogProps {
  children?: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onInviteSent?: () => void
  teamMembers?: TeamMemberOrInvitation[]
}

export function InviteUserDialog({
  children,
  open,
  onOpenChange,
  onInviteSent,
  teamMembers = [],
}: InviteUserDialogProps) {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Only block existing users - backend handles duplicate invitations smartly
  const validationError = useMemo(() => {
    const emailToCheck = email.trim().toLowerCase()
    if (!emailToCheck || !emailToCheck.includes('@')) return null

    const existingUser = teamMembers.find(
      (member) =>
        member.email.toLowerCase() === emailToCheck && member.type === 'user'
    )

    if (existingUser) {
      return 'This user is already a member of your organization'
    }

    return null
  }, [email, teamMembers])

  const handleInvite = async () => {
    if (!email.trim() || validationError) return

    setIsLoading(true)

    try {
      await invitationService.inviteUser(email.trim())

      toast.success('Invitation sent successfully', {
        description: `An invitation has been sent to ${email}`,
      })

      setEmail('')
      onOpenChange?.(false)
      onInviteSent?.()
    } catch (error: any) {
      console.error('Failed to send invitation:', error)

      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        'Failed to send invitation'

      toast.error('Failed to send invitation', {
        description: errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setEmail('')
    onOpenChange?.(false)
  }

  const isValid = email.trim() && email.includes('@') && !validationError

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
        </DialogHeader>

        <DialogBody className="grid gap-4">
          {/* Email Input */}
          <div className="flex flex-col gap-2">
            <Label
              htmlFor="email"
              className="text-sm font-medium text-foreground"
            >
              Email address
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="colleague@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`h-9 ${validationError ? 'border-red-500 focus:border-red-500' : ''}`}
              disabled={isLoading}
            />
            {validationError && (
              <p className="text-sm text-red-500 mt-1">{validationError}</p>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleInvite}
            disabled={!isValid || isLoading}
            className="flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Send Invitation
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
